import React from 'react';
import { Avatar, AvatarProps } from '@mui/material';
import { useQuery } from 'react-query';
import HoofIcon from './HoofIcon';
import { fetchHorsePhotoUrl, horsePhotoQueryKey } from '../utils/horsePhotos';
import { useRBAC } from '../auth/useRBAC';
import { patientChartPanels } from '../auth/pageControls';

export interface HorsePhotoAvatarProps extends Omit<AvatarProps, 'src'> {
  horseId: string | undefined | null;
  /**
   * Pre-resolved photo URL from an enriched horse row (the paginated list
   * already carries `photoUrl`). When provided — including an explicit `null`
   * — no network request is made. Omit it (leave `undefined`) to let the
   * avatar self-fetch by `horseId`, which is fine for the handful of avatars
   * on the chart banner and side rails.
   */
  photoUrl?: string | null;
  /** Tile size in pixels — Avatar is square, sx.width/height applied. */
  size?: number;
  /** Optional override for the fallback icon color. */
  iconColor?: string;
}

/**
 * Drop-in hoof-iconed avatar used across the patient surfaces. Patient photos
 * live in S3 (Attachment category 'patient_photo'); when one exists the avatar
 * renders it with `object-fit: cover`, otherwise it falls back to the hoof
 * icon — including when the image URL fails to load (MUI Avatar shows its
 * children on img error).
 */
const HorsePhotoAvatar: React.FC<HorsePhotoAvatarProps> = ({
  horseId,
  photoUrl,
  size = 40,
  iconColor = '#C9772B',
  sx,
  ...rest
}) => {
  // Only self-fetch when the caller didn't pass a URL at all. Passing `null`
  // means "known to have no photo" — skip the request and show the hoof.
  //
  // The photo is an Attachment, and `GET /attachments` requires
  // `view:medical_records` (attachments.controller.ts `findAll`), so a person
  // who cannot view medical records gets a 403 here on every chart visit. The
  // `files` panel entry carries that same gate: when the answer is KNOWN and
  // lacks it, skip the read and show the hoof (INV-108). Pending, unreadable and
  // identity-unknown fetch exactly as before.
  const rbac = useRBAC();
  const mayReadPhoto = patientChartPanels(rbac).files;
  const shouldFetch = photoUrl === undefined && !!horseId && mayReadPhoto;
  const { data: fetched } = useQuery(
    horsePhotoQueryKey(horseId),
    () => fetchHorsePhotoUrl(horseId as string),
    { enabled: shouldFetch, staleTime: 5 * 60_000 },
  );

  const src = (photoUrl !== undefined ? photoUrl : fetched) || undefined;

  return (
    <Avatar
      src={src}
      sx={{
        width: size,
        height: size,
        bgcolor: 'rgba(139,94,52,0.08)',
        // Border keeps photos crisp against the page background.
        border: src ? '1px solid rgba(139,94,52,0.18)' : 'none',
        ...sx,
      }}
      imgProps={src ? { style: { objectFit: 'cover' } } : undefined}
      {...rest}
    >
      {/* Always provided so a falsy/failed src falls back to the hoof. */}
      <HoofIcon sx={{ color: iconColor, fontSize: Math.round(size * 0.55) }} />
    </Avatar>
  );
};

export default HorsePhotoAvatar;
